import { Badge } from "design-system/atoms/Badge"
import { cn } from "design-system/utils"
import { TFunction } from "i18next"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { CampaignLogKindEnum, CampaignLogResponseDataSchema } from "schema"
import z from "zod"

interface Props {
  kind: z.infer<typeof CampaignLogResponseDataSchema>["kind"]
}

function buildLogKindCopyMap(t: TFunction) {
  return {
    [CampaignLogKindEnum.ConversationAchieved]: t(
      "campaigns.detail.logs.kinds.ConversationAchieved",
    ),
    [CampaignLogKindEnum.Launch]: t("campaigns.detail.logs.kinds.Launch"),
    [CampaignLogKindEnum.FilterOut]: t("campaigns.detail.logs.kinds.FilterOut"),
    [CampaignLogKindEnum.Outreach]: t("campaigns.detail.logs.kinds.Outreach"),
    [CampaignLogKindEnum.Trigger]: t("campaigns.detail.logs.kinds.Trigger"),
    [CampaignLogKindEnum.TriggerDelayed]: t("campaigns.detail.logs.kinds.TriggerDelayed"),
    [CampaignLogKindEnum.TriggerResumed]: t("campaigns.detail.logs.kinds.TriggerResumed"),
    [CampaignLogKindEnum.ConditionsNotMet]: t("campaigns.detail.logs.kinds.ConditionsNotMet"),
    [CampaignLogKindEnum.WorkflowActionFailed]: t(
      "campaigns.detail.logs.kinds.WorkflowActionFailed",
    ),
    [CampaignLogKindEnum.WorkflowExecutionSucceeded]: t(
      "campaigns.detail.logs.kinds.WorkflowExecutionSucceeded",
    ),
    [CampaignLogKindEnum.WorkflowExecutionFailed]: t(
      "campaigns.detail.logs.kinds.WorkflowExecutionFailed",
    ),
  } satisfies Record<z.infer<typeof CampaignLogResponseDataSchema>["kind"], string>
}

const LogKindColorMap = {
  [CampaignLogKindEnum.Launch]: "",
  [CampaignLogKindEnum.Trigger]: "bg-chart-3/10 text-chart-3",
  [CampaignLogKindEnum.TriggerResumed]: "bg-chart-2/10 text-chart-2",
  [CampaignLogKindEnum.TriggerDelayed]: "bg-testing-1/10 text-testing-1",
  [CampaignLogKindEnum.FilterOut]: "",
  [CampaignLogKindEnum.Outreach]: "bg-chart-secondary/20 text-chart-primary",
  [CampaignLogKindEnum.ConversationAchieved]: "bg-success/10 text-success/70",
  [CampaignLogKindEnum.WorkflowActionFailed]: "",
  [CampaignLogKindEnum.ConditionsNotMet]: "",
  [CampaignLogKindEnum.WorkflowExecutionSucceeded]: "",
  [CampaignLogKindEnum.WorkflowExecutionFailed]: "",
} satisfies Record<z.infer<typeof CampaignLogResponseDataSchema>["kind"], string>

export const CampaignLogKindBadge = ({ kind }: Props) => {
  const { t } = useTranslation()
  const logKindCopyMap = useMemo(() => buildLogKindCopyMap(t), [t])

  const copy = logKindCopyMap[kind]
  const colorClass = LogKindColorMap[kind]

  return (
    <Badge variant="outline" className={cn("font-mono", colorClass)}>
      {copy ?? "-"}
    </Badge>
  )
}
